import { generateExternalId } from "@/app/oauth/utils";
import type { Server, ServerForm as ServerFormType } from "@/app/servers/types";

export const DEFAULT_WORKER_CONFIG = {
  timeout: 30,
  retry: 3,
} as const;

export const createDefaultFormValues = (defaultValues?: {
  id?: string;
  uri?: string;
}): ServerFormType => ({
  id: defaultValues?.id ?? "",
  enabled: true,
  type: "mcp" as const,
  binding: { type: "project" },
  mcp: {
    ...DEFAULT_WORKER_CONFIG,
    uri: defaultValues?.uri ?? "",
    oauth2: { external_id: generateExternalId() },
  },
});

const extractSecretValue = (
  value: string | { exists: boolean } | undefined
): string => {
  if (!value || typeof value !== "string") {
    return "";
  }
  return value;
};

const extractSecretsRecord = (
  secrets: Record<string, string | { exists: boolean }> | undefined
): Record<string, string> | undefined => {
  if (!secrets) {
    return;
  }
  const result: Record<string, string> = {};
  for (const key in secrets) {
    if (Object.hasOwn(secrets, key)) {
      result[key] = "";
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

const createHttpFormValues = (server: Server) => {
  if (server.type !== "http" || !server.http) {
    return;
  }
  return {
    uri: server.http.uri,
    secret: server.http.secret.value ?? "",
    timeout: server.http.timeout,
    retry: server.http.retry,
  };
};

const createOAuth2FormValues = (
  oauth2: NonNullable<Server["mcp"]>["oauth2"]
) => {
  if (!oauth2) {
    return;
  }
  return {
    client_id: oauth2.client_id,
    client_secret: extractSecretValue(oauth2.client_secret),
    external_id:
      typeof oauth2.external_id === "string" ? oauth2.external_id : undefined,
    authorization_url:
      typeof oauth2.authorization_url === "string"
        ? oauth2.authorization_url
        : undefined,
  };
};

const createMcpFormValues = (server: Server) => {
  if (server.type !== "mcp" || !server.mcp) {
    return;
  }
  const oauth2 = createOAuth2FormValues(server.mcp.oauth2);
  // The engine returns external_id at the top-level mcp config so that
  // oauth2 != null reliably signals "has OAuth credentials".  Merge it
  // into the form's oauth2 so the callback URL displays correctly.
  const externalId = server.mcp.external_id ?? oauth2?.external_id;
  return {
    uri: server.mcp.uri,
    timeout: server.mcp.timeout,
    retry: server.mcp.retry,
    oauth2: externalId ? { ...oauth2, external_id: externalId } : oauth2,
    headers: server.mcp.headers,
    secrets: extractSecretsRecord(server.mcp.secrets),
  };
};

export const createFormValuesFromServer = (server: Server): ServerFormType => ({
  id: server.id,
  enabled: server.enabled,
  type: server.type,
  binding: server.binding,
  http: createHttpFormValues(server),
  mcp: createMcpFormValues(server),
});

export const hasAdvancedMCPConfig = (server?: Server): boolean =>
  server?.type === "mcp" &&
  (!!server.mcp?.oauth2 ||
    (!!server.mcp?.headers && Object.keys(server.mcp.headers).length > 0) ||
    (!!server.mcp?.secrets && Object.keys(server.mcp.secrets).length > 0));

const stripUndefinedNull = (
  obj: Record<string, unknown>
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const value = obj[key];
      if (value !== undefined && value !== null) {
        result[key] = value;
      }
    }
  }
  return result;
};

export const cleanOAuth2Values = (cleanedValues: ServerFormType) => {
  if (cleanedValues.mcp?.oauth2 && !cleanedValues.mcp.oauth2.client_id) {
    const { external_id, ...oauth2Rest } = cleanedValues.mcp.oauth2;
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    const stripped = stripUndefinedNull(oauth2Rest as Record<string, unknown>);
    const hasExternalId =
      typeof external_id === "string" && external_id.length > 0;
    const hasOtherFields = Object.keys(stripped).length > 0;

    let oauth2: typeof cleanedValues.mcp.oauth2 | undefined;
    if (hasExternalId) {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      oauth2 = {
        external_id,
        ...(hasOtherFields ? stripped : {}),
      } as typeof cleanedValues.mcp.oauth2;
    } else if (hasOtherFields) {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      oauth2 = stripped as typeof cleanedValues.mcp.oauth2;
    }

    return {
      ...cleanedValues,
      mcp: {
        ...cleanedValues.mcp,
        oauth2,
      },
    };
  }
  return cleanedValues;
};
